import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { direNombre } from '@nextilt/commun';
import { api, EchecApi } from '../partage/api/client';
import { useAuPouce } from '../partage/surface';
import { cles, relireApres } from '../partage/relectures';
import { motsDuDossier } from '../partage/vocabulaire';
import './ImporterPlan.css';

/**
 * Importer un plan écrit par son IA — ticket 24, jalon 8 bis de la SPEC.
 *
 * Deux gestes, dans l'ordre où Igor les fait : copier l'invite pour son IA, puis coller sa
 * réponse. Aucune planche ne dessine cet écran : il reprend la grammaire du dialogue du
 * modèle (voile clair, panneau posé, un seul bouton principal).
 *
 * L'écran reste bête : il envoie le texte tel quel, bavardage compris. C'est le serveur qui
 * trouve le JSON, le vérifie et dit quoi corriger, et ce message-là est fait pour être
 * renvoyé tel quel à l'IA.
 */
export function ImporterPlan({ onFermer }: { onFermer: () => void }) {
  const naviguer = useNavigate();
  const client = useQueryClient();
  const invite = useQuery({ queryKey: cles.inviteDuPlan, queryFn: api.inviteDuPlan, staleTime: Infinity });
  const [texte, setTexte] = useState('');
  const [copie, setCopie] = useState<'rien' | 'invite' | 'erreurs' | 'a_la_main' | 'erreurs_a_la_main'>(
    'rien'
  );
  const [tropLong, setTropLong] = useState(false);
  const zoneInvite = useRef<HTMLTextAreaElement>(null);
  const m = motsDuDossier(useAuPouce());

  const importer = useMutation({
    mutationFn: (contenu: string) => api.importerPlan(contenu),
    onSuccess: (resultat) => {
      void relireApres(client, 'importerUnPlan');
      naviguer(`/feuille/${resultat.dossierId}`);
    },
  });

  const erreurs = erreursDuPlan(importer.error);

  // Le refus arrive sous la zone collée, souvent hors de vue : on l'amène sous les yeux.
  const zoneRefus = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (importer.error || tropLong) {
      zoneRefus.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [importer.error, tropLong]);

  async function copier(contenu: string, quoi: 'invite' | 'erreurs') {
    try {
      // Absent hors d'une page sûre (une adresse réseau en http) : on passe alors à la main.
      await navigator.clipboard.writeText(contenu);
      setCopie(quoi);
    } catch {
      if (quoi === 'erreurs') {
        setCopie('erreurs_a_la_main');
        return;
      }
      setCopie('a_la_main');
      requestAnimationFrame(() => zoneInvite.current?.select());
    }
  }

  function envoyer() {
    const limite = invite.data?.limites.texte;
    if (limite && texte.length > limite) {
      importer.reset();
      setTropLong(true);
      return;
    }
    setTropLong(false);
    importer.mutate(texte);
  }

  return (
    <>
      {/* Un clic à côté ne jette pas un plan collé : seuls la croix et « annuler » ferment alors. */}
      <div className="plan-voile" onClick={() => !texte.trim() && onFermer()} aria-hidden="true" />

      <div className="plan" role="dialog" aria-label="importer un plan écrit par ton IA">
        <header className="plan-tete">
          <div>
            <p className="plan-amorce">Un plan écrit par ton IA</p>
            <h2>Importer un plan</h2>
          </div>
          <button type="button" className="plan-fermer" onClick={onFermer} aria-label="fermer">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <path d="M3.5 3.5l10 10M13.5 3.5l-10 10" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </header>

        <section className="plan-temps">
          <span className="plan-numero" aria-hidden="true">
            1
          </span>
          <div className="plan-temps-corps">
            <p>
              Copie l’invite et colle-la dans ton IA (ChatGPT, Claude…). Ton IA va te poser des
              questions sur ton projet, puis écrira ton plan quand tu lui diras que tu es prêt,
              sans inventer de date ni de délai.
            </p>
            <div className="plan-copie">
              <button
                type="button"
                className="bouton bouton-discret"
                onClick={() => invite.data && void copier(invite.data.invite, 'invite')}
                disabled={!invite.data}
              >
                Copier l’invite
              </button>
              {copie === 'invite' && <span className="plan-fait">Invite copiée</span>}
              {invite.isError && (
                <span className="plan-a-la-main">L’invite ne se charge pas : ferme et rouvre cette fenêtre</span>
              )}
            </div>
            {copie === 'a_la_main' && invite.data && (
              <>
                <p className="plan-a-la-main">
                  La copie automatique n’est pas permise ici. L’invite est sélectionnée
                  ci-dessous : copie-la toi-même.
                </p>
                <textarea
                  ref={zoneInvite}
                  className="champ plan-invite"
                  value={invite.data.invite}
                  readOnly
                  rows={6}
                  aria-label="l’invite à copier"
                />
              </>
            )}
          </div>
        </section>

        <section className="plan-temps">
          <span className="plan-numero" aria-hidden="true">
            2
          </span>
          <div className="plan-temps-corps">
            <p>Colle ici sa réponse entière. Le texte autour du plan ne gêne pas.</p>
            <textarea
              className="champ plan-colle"
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="Colle ici la réponse de ton IA"
              rows={9}
              aria-label="la réponse de ton IA"
              spellCheck={false}
            />
          </div>
        </section>

        <div ref={zoneRefus}>
          {tropLong && (
            <div className="plan-refus" role="alert">
              <p className="plan-refus-titre">Le texte collé est trop long.</p>
              <p>
                {direNombre(invite.data?.limites.texte ?? 0)} caractères au plus. Demande
                à ton IA un plan plus court, ou {m.enDeux}
              </p>
            </div>
          )}

          {erreurs && (
            <div className="plan-refus" role="alert">
              <p className="plan-refus-titre">
                {erreurs.length > 0 ? 'Le plan ne passe pas. Rien n’a été créé.' : 'L’import n’a pas eu lieu. Rien n’a été créé.'}
              </p>
              {erreurs.length > 0 ? (
                <>
                  <ul>
                    {erreurs.map((erreur, i) => (
                      <li key={i}>{erreur}</li>
                    ))}
                  </ul>
                  <div className="plan-copie">
                    <button
                      type="button"
                      className="bouton bouton-discret"
                      onClick={() => void copier(messagePourLIA(erreurs), 'erreurs')}
                    >
                      Copier ce message pour ton IA
                    </button>
                    {copie === 'erreurs' && <span className="plan-fait">Message copié</span>}
                  </div>
                  {copie === 'erreurs_a_la_main' && (
                    <p className="plan-a-la-main">
                      La copie automatique n’est pas permise ici : surligne la liste et copie-la.
                    </p>
                  )}
                </>
              ) : (
                <p>{autreEchec(importer.error)}</p>
              )}
            </div>
          )}
        </div>

        <footer className="plan-pied">
          <p>
            {m.nouveauALaRacine} Rien de ce que tu as déjà n’est touché, et
            tout ce que le plan pose reste modifiable.
          </p>
          <div className="plan-issues">
            <button type="button" className="plan-annuler" onClick={onFermer}>
              Annuler
            </button>
            <button
              type="button"
              className="bouton"
              onClick={envoyer}
              disabled={!texte.trim() || importer.isPending}
            >
              {importer.isPending ? 'Un instant' : 'Importer le plan'}
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

/**
 * Ce que le serveur a trouvé à corriger. `null` : aucun échec. Une liste vide : un échec
 * qui n'est pas un plan invalide (réseau, plafond), dit à part.
 */
function erreursDuPlan(echec: Error | null): string[] | null {
  if (!echec) return null;
  if (echec instanceof EchecApi && echec.code === 'plan_invalide') {
    const details = echec.details as { erreurs?: unknown } | undefined;
    if (Array.isArray(details?.erreurs)) return details.erreurs.map(String);
  }
  return [];
}

function autreEchec(echec: Error | null): string {
  if (!(echec instanceof EchecApi)) return 'Quelque chose a coincé. Réessaie.';
  if (echec.code === 'hors_ligne') return 'L’app ne répond pas. Vérifie qu’elle tourne, puis réessaie.';
  if (echec.code === 'plafond_de_bulles') return 'Tu as déjà trop de bulles pour ajouter ce plan.';
  if (echec.statut === 413) return 'Le texte collé est trop long. Demande à ton IA un plan plus court.';
  return 'Le serveur a refusé le texte. Réessaie, ou colle seulement le plan.';
}

/** Le message à renvoyer à l'IA : ce qu'il faut corriger, et ce qu'on attend en retour. */
function messagePourLIA(erreurs: string[]): string {
  return [
    'Le plan ne passe pas l’import de Nextilt. Corrige ces points, puis renvoie le plan complet, en JSON seul :',
    ...erreurs.map((e) => `- ${e}`),
  ].join('\n');
}
